import React from 'react'
import classnames from 'classnames'
import { Waypoint } from 'react-waypoint'
import { FormattedMessage } from 'react-intl'
import {
  NumbersLookRight,
  ChangeTheseValues,
  ScrolledIntoView,
  EVENTS_LABELS
} from 'src/constants/trackingEvents'
import { Button, Format, If, Unless, List, Lock, Text } from 'src/Components'
import { LockableInlineCurrencyText } from 'src/Components/shared/CopyHelpers'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import PieChart from 'src/Components/shared/PieChart'
import YourLoanSentence from 'src/Components/homeowners/YourLoanSentence'
import { oneTimeTagEventDispatcher } from 'src/helpers/tagManager'

import styles from './PaidSoFar.module.scss'

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.PaidSoFarSection
})

const PaidSoFar = ({
  withCurrentHomeData,
  expand,
  expandCheckSection,
  interest,
  interestRatio,
  loans,
  markLoans,
  principal,
  principalRatio,
  principalDiff,
  openLoans,
  shouldCheckNumbers,
  emptyState
}) => {
  return (
    <>
      <If condition={emptyState}>
        <Text
          align='center'
          className={styles.noInterestYet}
          size='s'
          data-qa='paidSoFar.unverifiedHeadline'
        >
          <FormattedMessage
            id='Homeowner.interest.noInterestYet'
            defaultMessage='Confirming your loan data is the first step towards smart homeownership.'
          />
        </Text>
      </If>
      <If condition={!emptyState}>
        <div data-qa='paidSoFar.body'>
          <div className={styles.chartContainer}>
            <div className={styles.leftFact}>
              <Text element='h3' size='l' weight='light'>
                <FormattedMessage
                  id='Homeowner.interest.towardsPrincipal'
                  defaultMessage='Towards Principal'
                />
              </Text>
              <Text
                className={styles.number}
                size='xxl'
                weight='light'
                color='green'
                element='div'
                data-qa='paidSoFar.principal'
              >
                <Lock enabled={!withCurrentHomeData}>
                  <Format.Currency value={principal} />
                </Lock>
              </Text>
              <Text color='quiet' size='xs'>
                <Unless condition={principalDiff > 0}>
                  <FormattedMessage
                    id='Homeowner.interest.towardsPrincipal.explain'
                    defaultMessage='How much of your loan you have repaid.'
                  />
                </Unless>
                <If condition={principalDiff > 0}>
                  <FormattedMessage
                    id='Homeowner.interest.towardsPrincipal.explainWithNumbers'
                    defaultMessage="{originalPrincipal} + {principalDiff} extra you've paid ahead of time."
                    values={{
                      originalPrincipal: LockableInlineCurrencyText(
                        principal - principalDiff,
                        'pink',
                        !withCurrentHomeData
                      ),
                      principalDiff: LockableInlineCurrencyText(
                        principalDiff,
                        'green',
                        !withCurrentHomeData
                      )
                    }}
                  />
                </If>
              </Text>
            </div>

            <div className={styles.chart}>
              <PieChart
                slices={[
                  {
                    value: interestRatio,
                    key: 'interestRatio',
                    className: styles.interestSlice
                  },
                  {
                    offset: true,
                    value: principalRatio,
                    key: 'principalRatio',
                    className: styles.principalSlice
                  }
                ]}
              />
            </div>

            <div className={styles.rightFact}>
              <Text element='h3' size='l' weight='light'>
                <FormattedMessage
                  id='Homeowner.interest.towardsInterest'
                  defaultMessage='Towards Interest'
                />
              </Text>
              <Text
                className={styles.number}
                size='xxl'
                weight='light'
                color='red'
                element='div'
                data-qa='paidSoFar.interest'
              >
                <Lock enabled={!withCurrentHomeData}>
                  <Format.Currency value={interest} />
                </Lock>
              </Text>
              <Text color='quiet' size='xs'>
                <FormattedMessage
                  id='Homeowner.interest.towardsInterest.explain'
                  defaultMessage='What the bank charges you for your loan.'
                />
              </Text>
            </div>
          </div>
          <If condition={!shouldCheckNumbers && withCurrentHomeData}>
            <Text
              size='xs'
              color='quiet'
              className={styles.loanSentence}
              data-qa='paidSoFar.descriptor'
            >
              <YourLoanSentence />
            </Text>
          </If>
        </div>
      </If>

      <Waypoint onEnter={expand} bottomOffset='300px'>
        <div />
      </Waypoint>

      <AdditionalInfo
        visible={shouldCheckNumbers && expandCheckSection}
        cardWidth
        className={classnames(
          styles.additionalInfo,
          !expandCheckSection && styles.aiHide
        )}
        showDisclaimers={false}
      >
        <Text element='h3' className={styles.foundLoans}>
          <FormattedMessage
            id='Homeowner.interest.doubleCheckNumbers'
            defaultMessage="Quick check, we've found these loans for you:"
          />
        </Text>

        <List.Bulleted className={styles.loanList}>
          {loans.map(loan => (
            <Text key={loan.id} className={styles.loanItem} size='s'>
              <FormattedMessage
                id='Homeowner.interest.doubleCheckLoanDescription'
                defaultMessage='{termYears} year loan at {rate} for {amount} from {date}'
                values={{
                  termYears: loan.termYears || loan.termYearsEstimated,
                  rate: (
                    <Text element='span' color='green'>
                      {loan.rate || loan.rateEstimated}%
                    </Text>
                  ),
                  amount: (
                    <Text element='span' color='green'>
                      <Format.Currency value={loan.amount} />
                    </Text>
                  ),
                  date: <Format.Date value={loan.date} format='MMMM YYYY' />
                }}
              />
            </Text>
          ))}
        </List.Bulleted>

        <Button.Group className={styles.buttonGroup}>
          <Button
            onClick={markLoans}
            track={NumbersLookRight}
            data-qa='confirm'
          >
            <FormattedMessage
              id='Homeowner.interest.confirm'
              defaultMessage='That looks right'
            />
          </Button>
          <Button
            onClick={openLoans}
            track={ChangeTheseValues}
            data-qa='change'
          >
            <FormattedMessage
              id='Homeowner.interest.deny'
              defaultMessage='Change these numbers'
            />
          </Button>
        </Button.Group>
      </AdditionalInfo>
      <Waypoint onEnter={scrollIntoView} bottomOffset='25%'>
        <div />
      </Waypoint>
    </>
  )
}

export default PaidSoFar
